import { useLayoutEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import styles from './Header.module.css';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useSidebar } from '../../contexts/SidebarContext';
import logo from '../../assets/logo-pokefolio.png';

/**
 * Top bar: logo, theme switch, avatar.
 * Navigation lives in the sidebar (see Sidebar.tsx); on mobile the burger opens it as a drawer.
 */
export function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { mobileOpen, toggleMobile } = useSidebar();
  const headerRef = useRef<HTMLElement | null>(null);

  // Mesure dynamique pour l'offset sous header fixe
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const setVar = () => {
      const h = Math.ceil(el.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--header-h', `${h}px`);
    };
    setVar();
    // Recalcul après un court délai pour s'assurer que le DOM est complètement rendu
    const timeoutId = setTimeout(setVar, 100);
    const ro = new ResizeObserver(setVar);
    ro.observe(el);
    window.addEventListener('resize', setVar);
    return () => {
      clearTimeout(timeoutId);
      ro.disconnect();
      window.removeEventListener('resize', setVar);
      document.documentElement.style.removeProperty('--header-h');
    };
  }, []);

  const userInitials = useMemo(() => {
    const email = user?.email ?? 'Profil';
    const base = (email.split('@')[0] ?? email).trim();
    return base.slice(0, 2).toUpperCase();
  }, [user]);

  return (
    <>
      <header ref={headerRef} className={styles.header} role="banner">
        {/* Burger (mobile): opens the navigation */}
        {isAuthenticated ? (
          <button
            type="button"
            className={styles.burger}
            aria-label={mobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
            aria-expanded={mobileOpen}
            aria-controls="app-sidebar"
            onClick={toggleMobile}
          >
            {mobileOpen ? (
              <X aria-hidden="true" size={20} />
            ) : (
              <Menu aria-hidden="true" size={20} />
            )}
          </button>
        ) : (
          <span className={styles.burgerPlaceholder} aria-hidden="true" />
        )}

        {/* Logo */}
        <Link to="/" className={styles.brand} aria-label="Accueil PokéFolio">
          <img src={logo} alt="PokéFolio" width="36" height="36" className={styles.logo} />
        </Link>

        {/* Theme toggle + Profil à droite */}
        <div className={styles.right}>
          <label className={styles.themeSwitch}>
            <span className={styles.themeSwitchSun}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <g fill="currentColor">
                  <circle r="5" cy="12" cx="12"></circle>
                  <path d="m21 13h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm-17 0h-1a1 1 0 0 1 0-2h1a1 1 0 0 1 0 2zm13.66-5.66a1 1 0 0 1 -.66-.29 1 1 0 0 1 0-1.41l.71-.71a1 1 0 1 1 1.41 1.41l-.71.71a1 1 0 0 1 -.75.29zm-12.02 12.02a1 1 0 0 1 -.71-.29 1 1 0 0 1 0-1.41l.71-.66a1 1 0 0 1 1.41 1.41l-.71.71a1 1 0 0 1 -.7.24zm6.36-14.36a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm0 17a1 1 0 0 1 -1-1v-1a1 1 0 0 1 2 0v1a1 1 0 0 1 -1 1zm-5.66-14.66a1 1 0 0 1 -.7-.29l-.71-.71a1 1 0 0 1 1.41-1.41l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.29zm12.02 12.02a1 1 0 0 1 -.7-.29l-.66-.71a1 1 0 0 1 1.36-1.36l.71.71a1 1 0 0 1 0 1.41 1 1 0 0 1 -.71.24z"></path>
                </g>
              </svg>
            </span>
            <span className={styles.themeSwitchMoon}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
                <path d="m223.5 32c-123.5 0-223.5 100.3-223.5 224s100 224 223.5 224c60.6 0 115.5-24.2 155.8-63.4 5-4.9 6.3-12.5 3.1-18.7s-10.1-9.7-17-8.5c-9.8 1.7-19.8 2.6-30.1 2.6-96.9 0-175.5-78.8-175.5-176 0-65.8 36-123.1 89.3-153.3 6.1-3.5 9.2-10.5 7.7-17.3s-7.3-11.9-14.3-12.5c-6.3-.5-12.6-.8-19-.8z"></path>
              </svg>
            </span>
            <input
              type="checkbox"
              className={styles.themeSwitchInput}
              checked={theme === 'dark'}
              onChange={toggleTheme}
              aria-label={theme === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
            />
            <span className={styles.themeSwitchSlider}></span>
          </label>
          {isAuthenticated && (
            <button
              className={styles.profile}
              title={user?.email ?? 'Profil'}
              onClick={() => navigate('/profile')}
              aria-label="Accéder au profil"
            >
              <div className={styles.avatar} aria-hidden="true">
                {userInitials}
              </div>
            </button>
          )}
        </div>
      </header>

      {/* Espace sous header fixe */}
      <div className={styles.headerOffset} aria-hidden="true" />
    </>
  );
}
